import React, { useState } from "react";
import Language from "../lib/Language";

// eslint-disable-next-line no-useless-escape
const isAllowed = (val) => /^[\-.,0-9]*$/.test(val);

const getFormattedNumber = (value) => {
    const USE_COMMA = Language.get("zz_dec_decimal_separator") === ",";
    const vl = String(value);

    if (USE_COMMA) {
        return vl.replace(".", ",");
    }
    return vl.replace(",", ".");
};

type TNumberInputProps = {
    defaultValue: number;
    onUpdate: (value: number) => void;
    decimals?: number;
    disabled?: boolean;
    classNames?: string;
};

const NumberInput = ({
    defaultValue,
    onUpdate,
    decimals,
    disabled = false,
    classNames = "",
}: TNumberInputProps) => {
    const [value, setValue] = useState<string | number>(getFormattedNumber(defaultValue));

    // Replaces componentDidUpdate(prevProps): re-sync only when the incoming value
    // changed AND it differs from what is on screen, so typing is not interrupted.
    const [prevDefault, setPrevDefault] = useState(defaultValue);
    if (defaultValue !== prevDefault) {
        setPrevDefault(defaultValue);
        if (defaultValue !== value) {
            setValue(getFormattedNumber(defaultValue));
        }
    }

    const onReturn = () => {
        let val = parseFloat(String(value).replace(",", "."));
        if (isNaN(val)) {
            val = defaultValue;
        }

        if (decimals !== undefined && decimals > 0) {
            const base = Math.pow(10, decimals);
            val = Math.round(val * base) / base;
        }

        setValue(val);
        onUpdate(val);
    };

    const onChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
        const val = evt.target.value;
        if (isAllowed(val)) {
            setValue(val);
        }
    };

    const onKeyDown = (evt: React.KeyboardEvent<HTMLInputElement>) => {
        if (evt.key !== "Enter") {
            return;
        }

        onReturn();
        evt.preventDefault();
        evt.stopPropagation();
    };

    return (
        <input
            className={classNames}
            disabled={disabled}
            type="text"
            pattern="[.,\-0-9]*"
            value={value}
            onChange={onChange}
            onBlur={onReturn}
            onKeyDown={onKeyDown}
        />
    );
};

export default NumberInput;
